import React, { memo, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import ModalNewFieldMaker from './WarehouseIntegration';
import useNotification from 'hooks/useNotification';
import PaymentTypeIntegration from './PaymentTypeIntegration';

const Target = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenTypeModal, setIsOpenTypeModal] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const sendNotification = useNotification();
  const { REACT_APP_BACKEND_URL } = process.env;

  const [warehouseQuery, typeQuery] = useQueries([
    {
      queryKey: '/admin/integration/warehouse',
      queryFn: async function () {
        const response = await axiosPrivate.get(`/admin/integration/warehouse`);
        return response.data.data;
      },
      enabled: !hasError,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: '/admin/integration/types',
      queryFn: async function () {
        const response = await axiosPrivate.get(`/admin/integration/types`);
        return response.data.data;
      },
      enabled: !hasError,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
  ]);

  const onDelete = async (id) => {
    try {
      await axiosPrivate.delete(`/admin/integration/typedelete/${id}`);
      typeQuery.refetch();
      sendNotification({
        msg: "To'lov turi o'chirildi!",
        variant: 'success',
      });
    } catch (error) {
      sendNotification({
        msg: 'Xatolik yuz berdi!',
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('settings.integration.title')}</div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-table-wrapper grid grid-cols-3 gap-3 pt-2 relative h-[calc(100vh-75px)] overflow-y-auto my-scroll'>
        <div className='h-[300px] bg-gray-50 shadow-lg rounded-lg flex flex-col justify-between border border-gray-300 hover:bg-white hover:shadow-xl transition-all duration-200'>
          <div
            className={`header h-${
              warehouseQuery?.data?.id ? '1/2' : '3/4'
            } flex items-center justify-center`}
          >
            <i
              className={`bi bi-house-${
                warehouseQuery?.data?.id ? 'gear' : 'add'
              } text-[70px]`}
            />
          </div>
          <div className='body min-h-1/4 mb-1 flex gap-2 items-center justify-between px-4'>
            <div className='w-full flex flex-col justify-between gap-2'>
              <div className='flex gap-1 items-center justify-between'>
                <div className='grow truncate border rounded-md border-gray-300 px-1 py-2'>
                  {REACT_APP_BACKEND_URL}
                </div>
                <div>
                  <Button
                    variant='base'
                    onClick={() => {
                      sendNotification({
                        msg: 'URLdan nusxa olindi!',
                        variant: 'success',
                      });

                      navigator.clipboard.writeText(
                        REACT_APP_BACKEND_URL?.data?.link
                      );
                    }}
                  >
                    <i className='bi bi-copy' />
                  </Button>
                </div>
              </div>
              <div className='flex gap-1 items-center justify-between'>
                <div className='grow truncate border rounded-md border-gray-300 px-1 py-2'>
                  {warehouseQuery?.data?.key}
                </div>
                <div>
                  <Button
                    variant='base'
                    onClick={() => {
                      sendNotification({
                        msg: 'Tokkendan nusxa olindi!',
                        variant: 'success',
                      });

                      navigator.clipboard.writeText(warehouseQuery?.data?.key);
                    }}
                  >
                    <i className='bi bi-copy' />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className='footer h-1/4 overflow-hidden'>
            <Button
              variant='contained'
              className='!w-full !h-full !rounded-t-none'
              color='primary'
              onClick={() => {
                setIsOpenModal(true);
              }}
              startIcon={
                <i
                  className={`bi bi-${
                    warehouseQuery?.data?.id ? 'gear' : 'plus-circle'
                  }`}
                />
              }
            >
              {t(
                `settings.integration.${
                  warehouseQuery?.data?.id ? 'editModal' : 'addModal'
                }.warehouse`
              )}
            </Button>
          </div>
        </div>
        <div className='h-[300px] bg-gray-50 shadow-lg rounded-lg flex flex-col justify-between border border-gray-300 hover:bg-white hover:shadow-xl transition-all duration-200'>
          <div className='body min-h-3/4 max-h-[228px] mb-1 flex gap-2 items-center justify-between px-4 py-4'>
            <div className='w-full flex flex-col justify-between gap-2 h-full overflow-y-auto my-scroll'>
              {typeQuery?.data?.map((item) => (
                <div
                  key={item?.id}
                  className='flex gap-1 items-center justify-between'
                >
                  <div className='grow truncate border rounded-md border-gray-300 px-1 py-2'>
                    {item?.type?.name}
                  </div>
                  <div>
                    <Button
                      variant='base'
                      color='error'
                      onClick={() => {
                        onDelete(item?.id);
                      }}
                    >
                      <i className='bi bi-trash' />
                    </Button>
                  </div>
                </div>
              ))}
              {/* <div className='flex gap-1 h-10 items-center justify-between' /> */}
            </div>
          </div>
          <div className='footer h-1/4 overflow-hidden max-h-[65.34px]'>
            <Button
              variant='contained'
              className='!w-full !h-full !rounded-t-none'
              color='primary'
              onClick={() => {
                setIsOpenTypeModal(true);
              }}
              startIcon={
                <i
                  className={`bi bi-${
                    warehouseQuery?.data?.id ? 'gear' : 'plus-circle'
                  }`}
                />
              }
            >
              {t('settings.integration.addModal.paymentType')}
            </Button>
          </div>
        </div>
      </div>
      {isOpenModal && (
        <ModalNewFieldMaker
          open={isOpenModal}
          setOpen={setIsOpenModal}
          data={warehouseQuery.data}
          refetch={warehouseQuery.refetch}
        />
      )}
      {isOpenTypeModal && (
        <PaymentTypeIntegration
          open={isOpenTypeModal}
          setOpen={setIsOpenTypeModal}
          data={typeQuery.data}
          refetch={typeQuery.refetch}
        />
      )}
    </div>
  );
};

export default memo(Target);
